import _ from "lodash";
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  exportReturnFieldsBySections,
  FieldsSections,
  SectionKeys,
} from "../utils/selectable-export-fields";

export type ExportReturnFieldsBySections = {
  [key in SectionKeys]: FieldsSections[];
};

const useExportReturnFields = ({
  fieldsAlreadySelected,
}: {
  fieldsAlreadySelected: string[];
}) => {
  const defaultExportReturnFields = useMemo(() => {
    const fieldsBySections = exportReturnFieldsBySections.map((section) => {
      const fields = section.fields.filter((field) => {
        if (field.includes("customer"))
          return fieldsAlreadySelected.includes("customer");
        if (field.includes("order"))
          return fieldsAlreadySelected.includes("order");
        return fieldsAlreadySelected.includes(field);
      });
      return { [section.titleKey]: fields };
    });
    return Object.assign({}, ...fieldsBySections);
  }, [fieldsAlreadySelected]);

  const [returnExportFields, setReturnExportFields] =
    useState<ExportReturnFieldsBySections>(defaultExportReturnFields);

  const toggleSelectAll = useCallback(
    () =>
      setReturnExportFields(
        areAllFieldsSelected() ? defaultExportReturnFields : selectAllFields,
      ),
    [returnExportFields],
  );

  const selectAllFields = useMemo(() => {
    return exportReturnFieldsBySections.reduce((acc, section) => {
      acc[section.titleKey] = section.fields.filter(
        (field) =>
          !field.includes("metafields") && field !== "customAttributesRR",
      );
      return acc;
    }, {} as ExportReturnFieldsBySections);
  }, []);

  const sectionState = (sectionKey: SectionKeys) => {
    return {
      maxFields: useMemo(
        () => findFieldsBySection(sectionKey)?.fields.length,
        [],
      ),

      selectedFieldsCount: useMemo(
        () => returnExportFields[sectionKey].length,
        [returnExportFields[sectionKey]],
      ),

      toggleField: (field: FieldsSections) => {
        setReturnExportFields((prev) => {
          const fields = prev[sectionKey];
          const isSelected = fields.includes(field);
          prev[sectionKey] = isSelected
            ? fields.filter((f) => f !== field)
            : [...fields, field];
          return { ...prev };
        });
      },

      toggleAllFields: () => {
        setReturnExportFields((prev) => {
          prev[sectionKey] = isCategorySelected(sectionKey)
            ? []
            : findFieldsBySection(sectionKey)?.fields || [];
          return { ...prev };
        });
      },

      isAnyFieldSelected: useMemo(() => {
        const fields = findFieldsBySection(sectionKey)?.fields;
        if (!fields) return false;
        return fields.some((field) =>
          returnExportFields[sectionKey].includes(field),
        );
      }, [returnExportFields]),
    };
  };

  const findFieldsBySection = useCallback((sectionKey: SectionKeys) => {
    return exportReturnFieldsBySections.find(
      (section) => section.titleKey === sectionKey,
    );
  }, []);

  const isFieldSelected = useCallback(
    (field: FieldsSections) => {
      return _.flatten(Object.values(returnExportFields)).includes(field);
    },
    [returnExportFields],
  );

  const isCategorySelected = useCallback(
    (sectionKey: SectionKeys) => {
      const fields = findFieldsBySection(sectionKey)?.fields;
      return _.isEqual(fields, returnExportFields[sectionKey]);
    },
    [returnExportFields],
  );

  const areAllFieldsSelected = useCallback(() => {
    const fields = exportReturnFieldsBySections.map(
      (section) => section.fields,
    );
    const allFields = _.flatten(fields).filter(
      (field) =>
        !field.includes("metafields") && field !== "customAttributesRR",
    );
    return _.isEqual(allFields, _.flatten(Object.values(returnExportFields)));
  }, [returnExportFields]);

  return {
    returnExportFields,
    isFieldSelected,
    isCategorySelected,
    sectionState,
    exportReturnFieldsBySections,
    areAllFieldsSelected,
    toggleSelectAll,
  };
};

type ExportReturnFieldsProps = ReturnType<typeof useExportReturnFields>;

const ExportReturnFieldsContext = createContext<
  ExportReturnFieldsProps | undefined
>(undefined);

export const ExportReturnFieldsProvider: FC<
  PropsWithChildren<{
    fieldsAlreadySelected: string[];
  }>
> = ({ children, fieldsAlreadySelected }) => {
  const useExportReturnFieldsProps = useExportReturnFields({
    fieldsAlreadySelected,
  });
  return (
    <ExportReturnFieldsContext.Provider
      value={{
        ...useExportReturnFieldsProps,
      }}
    >
      {children}
    </ExportReturnFieldsContext.Provider>
  );
};

export const useExportReturnFieldsContext = () => {
  const context = useContext(ExportReturnFieldsContext);
  if (context === undefined) {
    throw new Error("useWorkflowContext must be used within a WorkflowContext");
  }
  return context;
};
